import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ============================================================
// Email Marketing — gestion des campagnes email du marchand.
// CRUD persisté dans `email_campaigns` (isolé par commerce).
// ============================================================

interface Campaign {
  id: number;
  campaign_name: string;
  subject: string;
  message: string;
  status: string;
  total_recipients: number;
  total_sent: number;
  total_failed: number;
  created_at: string;
  sent_at: string | null;
}

const EMPTY_FORM = { campaign_name: '', subject: '', message: '' };

export default function EmailMarketingPage() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formMsg, setFormMsg] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [contactCount, setContactCount] = useState(0);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const cid = getCommerceId(user);
      const { data, error: err } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('user_id', cid)
        .order('created_at', { ascending: false });
      if (err) throw err;
      setCampaigns((data || []) as Campaign[]);

      const { count } = await supabase
        .from('clientshop')
        .select('id', { count: 'exact', head: true })
        .eq('idshop', cid);
      setContactCount(count || 0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormMsg(null); setModalOpen(true); };
  const openEdit = (c: Campaign) => {
    setEditing(c);
    setForm({ campaign_name: c.campaign_name || '', subject: c.subject || '', message: c.message || '' });
    setFormMsg(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!user) return;
    if (!form.campaign_name.trim() || !form.subject.trim()) { setFormMsg('Le nom et l’objet sont requis.'); return; }
    setSaving(true);
    setFormMsg(null);
    try {
      if (editing) {
        const { error: err } = await supabase.from('email_campaigns').update({
          campaign_name: form.campaign_name.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
        }).eq('id', editing.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('email_campaigns').insert({
          user_id: getCommerceId(user),
          campaign_name: form.campaign_name.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          status: 'draft',
          total_recipients: contactCount,
          total_sent: 0,
          total_failed: 0,
        });
        if (err) throw err;
      }
      setModalOpen(false);
      fetchData();
    } catch (e: unknown) {
      setFormMsg(e instanceof Error ? e.message : 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await supabase.from('email_campaigns').delete().eq('id', id);
    setDeleteId(null);
    fetchData();
  };

  const stats = useMemo(() => ({
    total: campaigns.length,
    sent: campaigns.filter((c) => c.status === 'sent').length,
    drafts: campaigns.filter((c) => c.status !== 'sent').length,
    reached: campaigns.reduce((s, c) => s + (c.total_sent || 0), 0),
  }), [campaigns]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950"><i className="ri-mail-send-line mr-2 text-primary-500"></i>Email Marketing</h2>
          <p className="text-sm text-foreground-500 mt-1">Créez et gérez vos campagnes email</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouvelle campagne
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { label: 'Campagnes', value: stats.total, icon: 'ri-mail-line', color: 'text-primary-600' },
          { label: 'Envoyées', value: stats.sent, icon: 'ri-send-plane-line', color: 'text-accent-600' },
          { label: 'Brouillons', value: stats.drafts, icon: 'ri-draft-line', color: 'text-secondary-600' },
          { label: 'Emails délivrés', value: stats.reached, icon: 'ri-mail-check-line', color: 'text-foreground-700' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <i className={`${s.icon} text-lg ${s.color}`}></i>
            <div className="text-2xl font-bold font-heading text-foreground-950 mt-2">{s.value}</div>
            <div className="text-xs text-foreground-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : error ? (
        <div className="flex flex-col items-center py-16"><i className="ri-error-warning-line text-3xl text-red-400 mb-3"></i><p className="text-sm text-foreground-600 mb-3">{error}</p><button onClick={fetchData} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">Réessayer</button></div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <i className="ri-mail-add-line text-4xl text-foreground-300 mb-3 block"></i>
          <h3 className="text-base font-semibold text-foreground-700 mb-1">Aucune campagne</h3>
          <p className="text-sm text-foreground-500 mb-4">Créez votre première campagne email</p>
          <button onClick={openCreate} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600">Nouvelle campagne</button>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-background-100 border-b border-background-200/70 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            <div className="col-span-5">Campagne</div>
            <div className="col-span-3">Statut</div>
            <div className="col-span-2">Destinataires</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-background-200/70">
            {campaigns.map((c) => (
              <div key={c.id} className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 px-5 py-4 items-center hover:bg-background-50/50">
                <div className="sm:col-span-5 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground-900 truncate">{c.campaign_name}</h4>
                  <p className="text-xs text-foreground-400 truncate">{c.subject}</p>
                </div>
                <div className="sm:col-span-3">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${c.status === 'sent' ? 'bg-accent-50 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'sent' ? 'bg-accent-500' : 'bg-foreground-300'}`}></span>
                    {c.status === 'sent' ? 'Envoyée' : 'Brouillon'}
                  </span>
                </div>
                <div className="sm:col-span-2 text-sm text-foreground-600">{c.total_sent || 0} / {c.total_recipients || 0}</div>
                <div className="sm:col-span-2 flex items-center justify-end gap-1">
                  <button onClick={() => openEdit(c)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                  <button onClick={() => setDeleteId(c.id)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-background-50 rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto animate-scale-in">
            <h3 className="text-lg font-bold font-heading text-foreground-950 mb-4">{editing ? 'Modifier la campagne' : 'Nouvelle campagne'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nom de la campagne</label>
                <input type="text" value={form.campaign_name} onChange={(e) => setForm((f) => ({ ...f, campaign_name: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Objet de l’email</label>
                <input type="text" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Message</label>
                <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={6} maxLength={5000}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none" />
              </div>
              {formMsg && <div className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs">{formMsg}</div>}
              <p className="text-xs text-foreground-400"><i className="ri-information-line mr-1"></i>{contactCount} contact(s) dans votre base clients.</p>
            </div>
            <div className="flex items-center justify-end gap-3 mt-5">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-background-100 text-foreground-600 rounded-full text-sm font-medium cursor-pointer hover:bg-background-200/70">Annuler</button>
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 disabled:opacity-50">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteId(null)}></div>
          <div className="relative bg-background-50 rounded-xl p-6 w-full max-w-sm animate-scale-in">
            <h3 className="text-lg font-bold font-heading text-foreground-950 mb-2">Supprimer la campagne ?</h3>
            <p className="text-sm text-foreground-500 mb-5">Cette action est irréversible.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium cursor-pointer">Annuler</button>
              <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 bg-red-500 text-white rounded-full text-sm font-medium cursor-pointer hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}